import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Moon, Sun } from 'lucide-react';
import { FOCUS_RING, POP_CLASS } from '@shared/ui/kit';
import { useThemeStore } from '@stores/themeStore';

/** Gap between the rail and the menu. */
const MENU_OFFSET = 10;

/**
 * The glyph rail's account control: just the avatar, which opens a menu with
 * who you are, the theme switch and Sign out.
 *
 * Stacking avatar + theme + sign out in a 52px column took ~120px of rail
 * height; on a short screen that pushed the last nav icons into the footer.
 * The labelled rail keeps its inline row — there is room for it there.
 *
 * The menu is portalled to <body> and fixed beside the rail (bottom-aligned
 * with the avatar), because the rail card clips its overflow.
 */
export function AccountMenu({
  avatar,
  name,
  detail,
  email,
  onSignOut,
  signingOut,
  onOpenChange,
}: {
  /** The avatar face (image or initial), drawn inside the trigger. */
  avatar: ReactNode;
  name?: string;
  /** Role (or email when there is no role), in the metadata voice. */
  detail?: string;
  email?: string;
  onSignOut: () => void;
  signingOut: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const setMenuOpen = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const close = useCallback(
    (returnFocus = true) => {
      setMenuOpen(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [setMenuOpen],
  );

  // Place beside the trigger, before paint, so it never flashes in the corner.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const rail = triggerRef.current.closest('[data-nav-rail-card]')?.getBoundingClientRect();
    setPosition({
      left: (rail?.right ?? rect.right) + MENU_OFFSET,
      bottom: window.innerHeight - rect.bottom,
    });
  }, [open]);

  // Move focus into the menu once it is placed.
  useEffect(() => {
    if (open && position) menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open, position]);

  // Outside press, resize or page scroll closes it, the way the app's other menus close.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close(false);
    };
    const onViewportChange = () => close(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onViewportChange);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [open, close]);

  // Unmounting while open (the rail peeked or expanded) must not leave the
  // parent thinking the menu is still up.
  useEffect(() => () => onOpenChange?.(false), [onOpenChange]);

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  const itemClass =
    'flex w-full items-center gap-[9px] rounded-md px-[10px] py-2 text-left text-[12.5px] transition-colors ' +
    'hover:bg-[var(--co-selected)] focus:bg-[var(--co-selected)] disabled:cursor-not-allowed disabled:opacity-50 ' +
    FOCUS_RING;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setMenuOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu${name ? ` for ${name}` : ''}`}
        title={name}
        className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden ${FOCUS_RING}`}
        style={{ background: 'var(--co-avatar)', color: 'var(--co-ink-invert)', borderRadius: 8 }}
      >
        {avatar}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Account"
            onKeyDown={onMenuKeyDown}
            className={`fixed z-[60] w-[220px] p-[6px] ${POP_CLASS}`}
            style={{ left: position.left, bottom: position.bottom, borderRadius: 8, boxShadow: 'var(--co-shadow-pop)' }}
          >
            <div className="px-[10px] pb-2 pt-[6px]">
              <p className="truncate text-[12.5px] font-semibold text-[var(--co-ink)]" title={name}>
                {name}
              </p>
              <p
                className="mt-[2px] truncate text-[9.5px] uppercase tracking-[.08em] text-[var(--co-ink-3)]"
                style={{ fontFamily: 'var(--co-font-mono)' }}
                title={email}
              >
                {detail}
              </p>
            </div>
            <div className="border-t border-[var(--co-line-subtle)] pt-1">
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                style={{ color: 'var(--co-ink-2)' }}
                onClick={() => {
                  toggleTheme();
                  close();
                }}
              >
                {isDark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
                {isDark ? 'Light mode' : 'Dark mode'}
              </button>
            </div>
            {/* Destructive last, below a rule (kit Menu rule). */}
            <div className="mt-1 border-t border-[var(--co-line-subtle)] pt-1">
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                style={{ color: 'var(--co-burgundy)' }}
                disabled={signingOut}
                aria-busy={signingOut}
                onClick={onSignOut}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
